import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  CloudDownloadOutlined,
  RestoreOutlined,
} from "@mui/icons-material";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  useGetImportSourcesQuery,
  useStartSchoolImportMutation,
  useGetImportStatusQuery,
  useGetImportLogQuery,
  useReverseImportMutation,
  useCancelImportMutation,
  useInterruptImportMutation,
  useResumeImportMutation,
} from "./schoolImportApiSlice";
import toast from "../../../helpers/toast";

const IMPORT_PHASES = [
  { key: "school", label: "School Profile" },
  { key: "departments", label: "Departments" },
  { key: "sections", label: "Sections & Subsections" },
  { key: "teachers", label: "Teachers" },
  { key: "classrooms", label: "Classrooms" },
  { key: "students", label: "Students" },
  { key: "grades", label: "Grading Keys" },
  { key: "subjects", label: "Subjects" },
  { key: "ca_records", label: "CA Records" },
  { key: "exam_records", label: "Exam Records" },
  { key: "comments", label: "Comments" },
  { key: "traits", label: "Trait & Skill Grades" },
  { key: "questions", label: "Questions" },
];

const STATUS_COLORS = {
  pending: "warning",
  complete: "success",
  failed: "error",
  reversing: "warning",
  reversed: "default",
  interrupted: "error",
};

function SchoolImport() {
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [session, setSession] = useState("");
  const [taskId, setTaskId] = useState(null);
  const [importDone, setImportDone] = useState(false);
  const [importFailed, setImportFailed] = useState(false);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [reverseTaskId, setReverseTaskId] = useState(null);
  const [reverseDone, setReverseDone] = useState(false);
  const [reverseFailed, setReverseFailed] = useState(false);
  const [confirmReverse, setConfirmReverse] = useState(false);

  const {
    data: importLog,
    isLoading: logLoading,
    refetch: refetchLog,
  } = useGetImportLogQuery();

  const { data: schools, isLoading: schoolsLoading } = useGetImportSourcesQuery(undefined, {
    skip: !!(importLog && importLog.status !== 'reversed'),
  });

  const [startImport, { isLoading: startingImport }] = useStartSchoolImportMutation();
  const [reverseImport, { isLoading: startingReverse }] = useReverseImportMutation();
  const [cancelImport, { isLoading: cancellingImport }] = useCancelImportMutation();
  const [interruptImport, { isLoading: interruptingImport }] = useInterruptImportMutation();
  const [resumeImport, { isLoading: startingResume }] = useResumeImportMutation();

  const isRunning = taskId && !importDone && !importFailed;
  const isReversing = reverseTaskId && !reverseDone && !reverseFailed;

  const { data: statusData } = useGetImportStatusQuery(taskId, {
    skip: !taskId || importDone || importFailed,
    pollingInterval: isRunning ? 3000 : 0,
  });

  const { data: reverseStatusData } = useGetImportStatusQuery(reverseTaskId, {
    skip: !reverseTaskId || reverseDone || reverseFailed,
    pollingInterval: isReversing ? 3000 : 0,
  });

  useEffect(() => {
    if (!statusData) return;
    const { state, info } = statusData;
    if (state === "PROGRESS" && info) {
      setCompletedPhases(info.completed || []);
      setCurrentMessage(info.message || "");
    } else if (state === "SUCCESS") {
      setCompletedPhases(IMPORT_PHASES.map((p) => p.key));
      setImportDone(true);
      toast.success("School import completed successfully!");
      refetchLog();
    } else if (state === "FAILURE") {
      setImportFailed(true);
      toast.error(`Import failed: ${typeof info === "string" ? info : "Unknown error"}`);
      refetchLog();
    }
  }, [statusData]);

  useEffect(() => {
    if (!reverseStatusData) return;
    const { state, info } = reverseStatusData;
    if (state === "SUCCESS") {
      setReverseDone(true);
      toast.success("Import reversed successfully!");
      refetchLog();
      setReverseTaskId(null);
    } else if (state === "FAILURE") {
      setReverseFailed(true);
      toast.error(`Reversal failed: ${typeof info === "string" ? info : "Unknown error"}`);
      refetchLog();
    }
  }, [reverseStatusData]);

  // Resume polling if a pending import with a celery_task_id exists (e.g. after page refresh).
  // Do NOT resume polling if status is 'interrupted' — the task is dead.
  useEffect(() => {
    if (
      importLog &&
      importLog.status === 'pending' &&
      importLog.celery_task_id &&
      !taskId
    ) {
      setTaskId(importLog.celery_task_id);
      setCurrentMessage('Resuming import progress...');
    }
  }, [importLog]);

  const handleResumeImport = async () => {
    setImportDone(false);
    setImportFailed(false);
    setCompletedPhases([]);
    setCurrentMessage('Resuming import from last checkpoint...');
    setTaskId(null);
    try {
      const response = await resumeImport().unwrap();
      setTaskId(response.task_id);
      refetchLog();
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Failed to resume import';
      toast.error(msg);
    }
  };

  const handleStartImport = async () => {
    if (!selectedSchoolId || !session.trim()) return;
    setImportDone(false);
    setImportFailed(false);
    setCompletedPhases([]);
    setCurrentMessage("Starting import...");
    setTaskId(null);
    try {
      const response = await startImport({
        school_id: Number(selectedSchoolId),
        session: session.trim(),
      }).unwrap();
      setTaskId(response.task_id);
    } catch (e) {
      const msg = e?.data?.message || e?.message || "Failed to start import";
      toast.error(msg);
    }
  };

  const handleCancelImport = async () => {
    try {
      await cancelImport().unwrap();
      setTaskId(null);
      setImportDone(false);
      setImportFailed(false);
      setCompletedPhases([]);
      setCurrentMessage('');
      refetchLog();
      toast.info('Import cancelled.');
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Failed to cancel import';
      toast.error(msg);
    }
  };

  const handleInterruptImport = async () => {
    try {
      await interruptImport().unwrap();
      setTaskId(null);
      setImportDone(false);
      setImportFailed(false);
      setCurrentMessage('');
      refetchLog();
      toast.info('Import interrupted. You can resume it from the last checkpoint.');
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Failed to interrupt import';
      toast.error(msg);
    }
  };

  const handleReverseImport = async () => {
    setConfirmReverse(false);
    setReverseDone(false);
    setReverseFailed(false);
    try {
      const response = await reverseImport().unwrap();
      setReverseTaskId(response.task_id);
      toast.info("Import reversal started...");
    } catch (e) {
      const msg = e?.data?.message || e?.message || "Failed to start reversal";
      toast.error(msg);
    }
  };

  const canStart = selectedSchoolId && session.trim() && !isRunning;

  const FORM_LOCK_DATE = new Date('2026-12-30');
  const isFormLocked = new Date() >= FORM_LOCK_DATE;

  const canReverse =
    importLog &&
    Date.now() - new Date(importLog.created_at).getTime() < 48 * 60 * 60 * 1000;

  // Determine which view to show
  const hasExistingImport =
    importLog &&
    ["pending", "complete", "failed", "reversing", "interrupted"].includes(importLog.status);
  const showImportForm = !hasExistingImport && !logLoading;

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        School Import
      </Typography>

      {logLoading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Checking import status...</Typography>
        </Box>
      )}

      {/* Existing import panel */}
      {hasExistingImport && (
        <StyledPaper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={600}>
                Existing Import
              </Typography>
              <Chip
                label={importLog.status}
                color={STATUS_COLORS[importLog.status] || "default"}
                size="small"
              />
            </Stack>
            <Divider />
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Session:</strong> {importLog.session_name}
              </Typography>
              <Typography variant="body2">
                <strong>Source school ID:</strong> {importLog.source_school_id}
              </Typography>
              <Typography variant="body2">
                <strong>Imported at:</strong>{" "}
                {new Date(importLog.created_at).toLocaleString()}
              </Typography>
            </Stack>

            {(importLog.status === "complete" || importLog.status === "failed" || reverseFailed) ? (
              !canReverse ? (
                <Typography variant="body2" color="text.secondary">
                  Reversal is no longer available (more than 48 hours since import).
                </Typography>
              ) : confirmReverse ? (
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" color="error" alignSelf="center">
                    This will hard-delete all imported data. Are you sure?
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={handleReverseImport}
                    disabled={startingReverse}
                  >
                    {startingReverse ? <CircularProgress size={14} /> : "Yes, Reverse"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setConfirmReverse(false)}
                  >
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RestoreOutlined />}
                  onClick={() => setConfirmReverse(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Reverse Import
                </Button>
              )
            ) : null}

            {/* Interrupted import — offer resume or start fresh */}
            {importLog.status === "interrupted" && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  This import was interrupted (e.g. server restart). You can resume from the last checkpoint or start a fresh import.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleResumeImport}
                    disabled={startingResume}
                  >
                    {startingResume ? <CircularProgress size={14} /> : "Resume Import"}
                  </Button>
                </Stack>
              </Stack>
            )}

            {/* Reversal progress */}
            {isReversing && (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2">Reversing import...</Typography>
              </Stack>
            )}
          </Stack>
        </StyledPaper>
      )}

      {/* Import form — only shown when no active import exists */}
      {showImportForm && (
        <StyledPaper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" fontWeight={500} mb={0.5}>
                School
              </Typography>
              {schoolsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <select
                  value={selectedSchoolId}
                  onChange={(e) => setSelectedSchoolId(e.target.value)}
                  style={{
                    border: "0.5px solid #CCCCCC",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    fontSize: "14px",
                    color: selectedSchoolId ? "#333" : "#9A9A9A",
                    width: "100%",
                    height: "56px",
                    padding: "6px 16px",
                    outline: "none",
                  }}
                  disabled={isRunning || isFormLocked}
                >
                  <option value="">Select a school...</option>
                  {(schools || []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={500} mb={0.5}>
                Session <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                placeholder="e.g. 2023/2024"
                value={session}
                onChange={(e) => setSession(e.target.value)}
                disabled={isRunning || isFormLocked}
              />
            </Box>

            {isFormLocked && (
              <Typography variant="body2" color="text.secondary">
                The import form is no longer available.
              </Typography>
            )}
            <StyledLoadingButton
              onClick={handleStartImport}
              disabled={!canStart || startingImport || isFormLocked}
              style={{ height: 48, fontSize: 14, padding: "6px 24px" }}
            >
              {startingImport ? (
                <CircularProgress size={18} sx={{ color: "primary.contrastText" }} />
              ) : (
                <Stack direction="row" alignItems="center" gap={1}>
                  <CloudDownloadOutlined fontSize="small" />
                  Start Import
                </Stack>
              )}
            </StyledLoadingButton>
          </Stack>
        </StyledPaper>
      )}

      {/* Import progress stepper */}
      {taskId && (
        <StyledPaper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            {importDone ? (
              <CheckCircleOutline color="success" />
            ) : importFailed ? (
              <ErrorOutline color="error" />
            ) : (
              <CircularProgress size={18} />
            )}
            <Typography variant="body1" fontWeight={600}>
              {importDone
                ? "Import complete"
                : importFailed
                ? "Import failed"
                : currentMessage || "Running import..."}
            </Typography>
          </Stack>

          {isRunning && (
            <Stack direction="row" justifyContent="flex-end" spacing={1} mb={1}>
              <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={handleInterruptImport}
                disabled={interruptingImport || cancellingImport}
                sx={{ textTransform: 'none' }}
              >
                {interruptingImport ? 'Interrupting...' : 'Interrupt (Resume Later)'}
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleCancelImport}
                disabled={cancellingImport || interruptingImport}
                sx={{ textTransform: 'none' }}
              >
                {cancellingImport ? 'Cancelling...' : 'Cancel Import'}
              </Button>
            </Stack>
          )}
          <Stepper orientation="vertical" nonLinear>
            {IMPORT_PHASES.map((phase) => {
              const done = completedPhases.includes(phase.key);
              return (
                <Step key={phase.key} active={false} completed={done}>
                  <StepLabel
                    StepIconProps={{
                      sx: done
                        ? { color: "success.main" }
                        : { color: "action.disabled" },
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={done ? "text.primary" : "text.disabled"}
                    >
                      {phase.label}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </StyledPaper>
      )}
    </Box>
  );
}

export default SchoolImport;

